/**
 * Бланки декларації платника єдиного податку (ч.1113) — за офіційними
 * формами наказу МФУ №578 у ред. наказу від 31.01.2025 №57:
 *  - юрособа 3 групи J0103509 (розділи II–V, графи 3 % / 5 %);
 *  - ФОП F0103309 (квартал) / F0103407 (рік) (розділи I–VIII) + Додаток 1 ЄСВ.
 * Той самий вигляд, що бланк декларації з ПДВ (стилі VatDeclarationBlank):
 * редактор — поля жовті, друк — текстом. Суми — грн з копійками.
 */
import type { CSSProperties } from 'react';
import type { Organization } from '@/api/catalogs';
import type {
  SingleTaxDeclaration, SingleTaxEsvAppendix, SingleTaxFields, SingleTaxMpzAppendix, SingleTaxMpzPlot,
} from '@/api/singleTaxDeclarations';
import { PERIOD_LABELS } from '@/api/singleTaxDeclarations';
import {
  B, SERIF, XMark, blankBox, nameCell, numCell, thBlank, valCell,
} from './VatDeclarationBlank';
import { chiefForReports } from '@/shared/utils/orgOfficials';

const MONTHS = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

export const fmtMoney = (v: string | number | undefined | null) => {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);
};

/** Грошове поле бланка: редаговане (жовте) або текст. */
export function MoneyCell({ code, values, setV, readOnly, print }: {
  code: string; values: Record<string, string>;
  setV?: (code: string, raw: string) => void; readOnly: boolean; print?: boolean;
}) {
  const v = values[code] ?? '';
  if (readOnly || !setV) {
    return (
      <span style={{ display: 'block', padding: '2px 4px', textAlign: 'right', fontFamily: SERIF,
        fontSize: '0.82rem', fontWeight: 600, background: print ? 'transparent' : '#eef1f4',
        minHeight: '1.1em', color: '#111' }}>{print ? fmtMoney(v) : v}</span>
    );
  }
  return (
    <input type="number" step="0.01" value={v} data-code={code}
      onChange={(e) => setV(code, e.target.value)}
      style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
        fontFamily: SERIF, fontSize: '0.82rem', textAlign: 'right', padding: '2px 4px',
        background: '#fefce8', color: '#111' }} />
  );
}

const Mark = ({ on, onToggle, readOnly }: { on: boolean; onToggle?: () => void; readOnly?: boolean }) => (
  <span onClick={() => { if (!readOnly && onToggle) onToggle(); }}
    style={{ cursor: !readOnly && onToggle ? 'pointer' : 'default' }}>
    <XMark on={on} />
  </span>
);

const ro = (v: string | number | null | undefined, align: CSSProperties['textAlign'] = 'left', print?: boolean) => (
  <span style={{ display: 'block', padding: '2px 4px', fontWeight: 600, textAlign: align,
    background: print ? 'transparent' : '#eef1f4', color: '#334155', minHeight: '1.1em' }}>
    {v == null || v === '' ? ' ' : v}
  </span>
);

export interface RowSpec { num: string; label: string; codes: (string | null)[]; bold?: boolean }

function RowsTable({ title, headers, rows, values, setV, readOnly, print }: {
  title: string; headers: string[]; rows: RowSpec[];
  values: Record<string, string>; setV?: (c: string, raw: string) => void;
  readOnly: boolean; print?: boolean;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 8 }}>
      <thead>
        <tr><th colSpan={2 + headers.length} style={{ ...thBlank, fontWeight: 700, fontSize: '0.8rem' }}>{title}</th></tr>
        <tr>
          <th style={{ ...thBlank, textAlign: 'left' }}>Назва показника</th>
          <th style={{ ...thBlank, width: 52 }}>Код рядка</th>
          {headers.map((h) => <th key={h} style={{ ...thBlank, width: 130 }}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.num + r.label}>
            <td style={{ ...nameCell, fontWeight: r.bold ? 700 : 400 }}>{r.label}</td>
            <td style={numCell}>{r.num}</td>
            {r.codes.map((c, i) => (
              <td key={i} style={valCell}>
                {c ? <MoneyCell code={c} values={values} setV={setV} readOnly={readOnly} print={print} />
                  : <span style={{ display: 'block', textAlign: 'center', color: '#999' }}>×</span>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── ФОП (F0103309 / F0103407) ───────────────────────────────────────────────
export const FOP_SECTIONS: { title: string; rows: RowSpec[] }[] = [
  { title: 'II. Показники господарської діяльності для платників єдиного податку першої групи', rows: [
    { num: '01', label: 'Обсяг доходу за звітний (податковий) період відповідно до статті 292 ПКУ (пп.1 п.291.4 ст.291)', codes: ['R001G3'] },
    { num: '02', label: 'Обсяг доходу, що оподаткований за ставкою 15 відсотків (п.293.4 ст.293), у звітному періоді', codes: ['R002G3'] },
  ] },
  { title: 'III. Показники господарської діяльності для платників єдиного податку другої групи', rows: [
    { num: '03', label: 'Обсяг доходу за звітний (податковий) період відповідно до статті 292 ПКУ (пп.2 п.291.4 ст.291)', codes: ['R003G3'] },
    { num: '04', label: 'Обсяг доходу, що оподаткований за ставкою 15 відсотків (п.293.4 ст.293), у звітному періоді', codes: ['R004G3'] },
  ] },
  { title: 'IV. Показники господарської діяльності для платників єдиного податку третьої групи', rows: [
    { num: '05', label: 'Обсяг доходу за звітний (податковий) період, що оподатковується за ставкою 3 %', codes: ['R005G3'] },
    { num: '06', label: 'Обсяг доходу за звітний (податковий) період, що оподатковується за ставкою 5 %', codes: ['R006G3'] },
    { num: '07', label: 'Обсяг доходу, що оподаткований за ставкою 15 відсотків (п.293.4 ст.293), у звітному періоді', codes: ['R007G3'] },
  ] },
  { title: 'V. Визначення податкових зобов’язань по єдиному податку', rows: [
    { num: '08', label: 'Загальна сума доходу за звітний (податковий) період (рядки 01 + 02 + 03 + 04 + 05 + 06 + 07)', codes: ['R008G3'], bold: true },
    { num: '09', label: 'Сума податку за ставкою 15 % ((рядок 02 + рядок 04 + рядок 07) × 15 %)', codes: ['R009G3'] },
    { num: '10', label: 'Сума податку за ставкою 3 % (рядок 05 × 3 %)', codes: ['R010G3'] },
    { num: '11', label: 'Сума податку за ставкою 5 % (рядок 06 × 5 %)', codes: ['R011G3'] },
    { num: '12', label: 'Нараховано всього за звітний (податковий) період (рядок 09 + рядок 10 + рядок 11)', codes: ['R012G3'], bold: true },
    { num: '13', label: 'Нараховано за попередній звітний (податковий) період (значення рядка 12 декларації попереднього звітного періоду)', codes: ['R013G3'] },
    { num: '14.1', label: 'Сума єдиного податку, яка підлягає нарахуванню та сплаті в бюджет за підсумками поточного звітного періоду (рядок 12 − рядок 13)', codes: ['R0141G3'] },
    { num: '14.2', label: 'Позитивне значення різниці між сумою загального мінімального податкового зобов’язання та загальною сумою сплачених податків (рядок 04 графи 3 розділу II додатка 2)', codes: ['R0142G3'] },
    { num: '14', label: 'Загальна сума єдиного податку, яка підлягає нарахуванню та сплаті в бюджет (рядок 14.1 + рядок 14.2)', codes: ['R014G3'], bold: true },
  ] },
  { title: 'VI. Визначення податкових зобов’язань у зв’язку з виправленням самостійно виявлених помилок', rows: [
    { num: '15', label: 'Сума єдиного податку, яка підлягала перерахуванню до бюджету, за даними звітного періоду, в якому виявлена помилка (рядок 14 відповідної декларації)', codes: ['R015G3'] },
    { num: '16', label: 'Уточнена сума податкових зобов’язань за звітний період, у якому виявлена помилка', codes: ['R016G3'] },
    { num: '17', label: 'Збільшення суми, яка підлягала перерахуванню до бюджету (рядок 16 − рядок 15, якщо рядок 16 > рядка 15)', codes: ['R017G3'] },
    { num: '18', label: 'Зменшення суми, яка підлягала перерахуванню до бюджету (рядок 16 − рядок 15, якщо рядок 16 < рядка 15)', codes: ['R018G3'] },
    { num: '19', label: 'Сума штрафу (рядок 17 × 3 % або 17 × 5 %); у графі «%» — розмір штрафу (3 або 5)', codes: ['R019G1', 'R019G3'] },
    { num: '20', label: 'Сума пені, нарахована платником податку самостійно відповідно до підпункту 129.1.3 п.129.1 ст.129 ПКУ', codes: ['R020G3'] },
  ] },
];
export const FOP_VII: RowSpec = { num: '21', label: 'Сума єдиного внеску, яка підлягає сплаті на небюджетні рахунки, за даними звітного (податкового) періоду (рядок «Усього» графи 4 розділу 9 додатка 1)', codes: ['R021G3'], bold: true };
export const FOP_VIII_12: RowSpec = { num: '22', label: 'Сума військового збору, нарахованого за ставкою 10 % розміру мінімальної заробітної плати на 1 січня звітного року, у розрахунку за календарний місяць (МЗП × 10 % × кількість місяців перебування на першій, другій групах)', codes: ['R022G3'], bold: true };
export const FOP_VIII_3: RowSpec[] = [
  { num: '23', label: 'Сума військового збору за ставкою 1 % для платників єдиного податку третьої групи ((рядок 05 + рядок 06 + рядок 07) × 1 %)', codes: ['R023G3'] },
  { num: '24', label: 'Нараховано військового збору за попередній звітний (податковий) період (значення рядка 23 декларації попереднього звітного періоду)', codes: ['R024G3'] },
  { num: '25', label: 'Сума військового збору, яка підлягає нарахуванню та сплаті в бюджет за підсумками поточного звітного періоду (рядок 23 − рядок 24)', codes: ['R025G3'], bold: true },
  { num: '26', label: 'Сума військового збору, яка підлягала перерахуванню до бюджету, за даними звітного періоду, в якому виявлена помилка (рядок 25 відповідної декларації)', codes: ['R026G3'] },
  { num: '27', label: 'Уточнена сума податкових зобов’язань з військового збору за звітний період, у якому виявлена помилка', codes: ['R027G3'] },
  { num: '28', label: 'Збільшення суми військового збору, яка підлягала перерахуванню до бюджету (рядок 27 − рядок 26, якщо рядок 27 > рядка 26)', codes: ['R028G3'] },
  { num: '29', label: 'Зменшення суми військового збору, яка підлягала перерахуванню до бюджету (рядок 27 − рядок 26, якщо рядок 27 < рядка 26)', codes: ['R029G3'] },
];

// ── Юрособа (J0103509) ──────────────────────────────────────────────────────
const L = (n: number, label: string, single = false, bold = false): RowSpec =>
  ({ num: String(n), label, codes: single ? [`R${String(n).padStart(3, '0')}G3`] : [`R${String(n).padStart(3, '0')}G3`, `R${String(n).padStart(3, '0')}G4`], bold });
export const LEGAL_SECTIONS: { title: string; headers: string[]; rows: RowSpec[] }[] = [
  { title: 'II. Дохід, що підлягає оподаткуванню', headers: ['3 %', '5 %'], rows: [
    L(1, 'Сума доходу за податковий (звітний) період'),
    L(2, 'Сума доходу, що перевищує граничний обсяг доходу, визначений підпунктом 3 пункту 291.4 статті 291 ПКУ'),
    L(3, 'Сума доходу, отриманого при застосуванні іншого способу розрахунків, ніж грошовий (п.291.6 ст.291 ПКУ)'),
    L(4, 'Сума доходу, отриманого від провадження видів діяльності, які не дають права застосовувати спрощену систему оподаткування'),
    L(5, 'Загальна сума доходу за податковий (звітний) період (рядок 1 + рядок 2 + рядок 3 + рядок 4)', false, true),
  ] },
  { title: 'III. Розрахунок податкових зобов’язань з єдиного податку', headers: ['3 %', '5 %'], rows: [
    L(6, 'Сума єдиного податку (рядок 1 × С)'),
    L(7, 'Сума єдиного податку, що обчислюється у подвійному розмірі ставки ((рядок 2 + рядок 3 + рядок 4) × С × 2)'),
    L(8, 'Загальна сума нарахованого єдиного податку за податковий (звітний) період (рядок 6 + рядок 7)', false, true),
    L(9, 'Сума нарахованого єдиного податку за минулий податковий (звітний) період'),
    L(10, 'Сума нарахованого єдиного податку до сплати за податковий (звітний) квартал (рядок 8 − рядок 9)'),
    L(11, 'Позитивне значення різниці між сумою загального мінімального податкового зобов’язання та загальною сумою сплачених податків (графа 4 рядка 3 розділу II додатка)'),
    L(12, 'Загальна сума нарахованого єдиного податку до сплати (рядок 10 + рядок 11)', false, true),
  ] },
  { title: 'IV. Визначення податкових зобов’язань у зв’язку з виправленням самостійно виявлених помилок', headers: ['Сума'], rows: [
    L(13, 'Сума єдиного податку за даними раніше поданої податкової декларації, що уточнюється (рядок 12 цієї декларації, що уточнюється)', true),
    L(14, 'Уточнена сума податкових зобов’язань за податковий (звітний) період, у якому виявлена помилка', true),
    L(15, 'Сума єдиного податку (недоплата), яка збільшує податкове зобов’язання (рядок 14 − рядок 13, якщо рядок 14 > рядка 13)', true),
    L(16, 'Сума податку, яка зменшує податкове зобов’язання (рядок 13 − рядок 14, якщо рядок 13 > рядка 14)', true),
    L(17, 'Сума штрафу в розмірі 3 % суми недоплати (рядок 15 × 3 %)', true),
    L(18, 'Сума штрафу в розмірі 5 % суми недоплати (рядок 15 × 5 %)', true),
  ] },
  { title: 'V. Визначення податкових зобов’язань по військовому збору', headers: ['платники ПДВ (3 %)', 'неплатники ПДВ (5 %)'], rows: [
    L(19, 'Сума нарахованого військового збору за податковий (звітний) період (рядок 5 × 1 %)'),
    L(20, 'Сума нарахованого військового збору за минулий податковий (звітний) період'),
    L(21, 'Сума нарахованого військового збору до сплати за податковий (звітний) квартал (рядок 19 − рядок 20)', false, true),
    L(22, 'Сума військового збору, яка підлягала перерахуванню до бюджету, за даними раніше поданого звітного періоду, в якому виявлена помилка (рядок 21 відповідної декларації)'),
    L(23, 'Уточнена сума військового збору за звітний (податковий) період, у якому виявлена помилка'),
    L(24, 'Збільшення суми військового збору (рядок 23 − рядок 22, якщо рядок 23 > рядка 22)'),
    L(25, 'Зменшення суми військового збору (рядок 22 − рядок 23, якщо рядок 23 < рядка 22)'),
  ] },
];

export const KNOWN_CODES = new Set<string>([
  ...FOP_SECTIONS.flatMap((s) => s.rows.flatMap((r) => r.codes)),
  ...FOP_VII.codes, ...FOP_VIII_12.codes, ...FOP_VIII_3.flatMap((r) => r.codes),
  ...LEGAL_SECTIONS.flatMap((s) => s.rows.flatMap((r) => r.codes)),
  'R02G1', 'R02G2', 'R02G3', 'R02G4', 'R03G1', 'R03G2', 'R03G3', 'R03G4',
].filter(Boolean) as string[]);

/** Шапка бланка (тип, період, платник, реквізити) — обидві форми. */
export function SingleTaxHeader({ doc, org, fields, onFields, readOnly, print }: {
  doc: SingleTaxDeclaration; org: Organization | undefined; fields: SingleTaxFields;
  onFields?: (patch: Partial<SingleTaxFields>) => void; readOnly: boolean; print?: boolean;
}) {
  const legal = doc.form_code === 'J0103509';
  const annual = doc.form_code === 'F0103407';
  const tin = legal ? org?.edrpou : (org?.inn || org?.edrpou);
  const seqNo = fields.year_seq_no ?? '';
  return (
    <div style={{ ...blankBox }}>
      {legal && (
        // ч.1185: «Порядковий № за рік» — правий верхній кут бланка юрособи (HNY,
        // обовʼязкове у XSD: без нього ДПС відповідає ERROR_XSD). Номер серед усіх
        // декларацій єдиного податку, поданих у році, рахуючи подані не з сервісу.
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, padding: '4px 6px 0', fontSize: '0.8rem' }}>
          <span title="Номер цієї декларації серед поданих за рік декларацій будь-якого типу (звітна, звітна нова, уточнююча), включно з поданими не з цього сервісу">
            Порядковий № за рік
          </span>
          {readOnly || print || !onFields
            ? <b data-testid="year-seq-no" style={{ minWidth: 24, textAlign: 'center' }}>{seqNo === '' ? '—' : seqNo}</b>
            : (
              <input type="number" min={1} step={1} value={seqNo} aria-label="Порядковий № за рік"
                style={{ width: 64, textAlign: 'center' }}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  onFields({ year_seq_no: v === '' ? null : Math.max(1, Math.floor(Number(v)) || 1) });
                }} />
            )}
        </div>
      )}
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.95rem', padding: '6px 0 2px', fontFamily: SERIF }}>
        {legal ? 'ПОДАТКОВА ДЕКЛАРАЦІЯ ПЛАТНИКА ЄДИНОГО ПОДАТКУ ТРЕТЬОЇ ГРУПИ (ЮРИДИЧНОЇ ОСОБИ)'
          : 'ПОДАТКОВА ДЕКЛАРАЦІЯ ПЛАТНИКА ЄДИНОГО ПОДАТКУ – ФІЗИЧНОЇ ОСОБИ – ПІДПРИЄМЦЯ'}
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#555', paddingBottom: 6 }}>
        форма {doc.form_code} · наказ Мінфіну від 19.06.2015 № 578 у редакції наказу від 31.01.2025 № 57
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup><col style={{ width: 46 }} /><col /><col style={{ width: 340 }} /></colgroup>
        <tbody>
          <tr>
            <td style={numCell}>1</td>
            <td style={nameCell}>Тип податкової декларації</td>
            <td style={{ ...valCell, padding: '2px 6px' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: '0.8rem' }}>
                <span>звітна <XMark on={doc.report_type === 'REPORTING'} /></span>
                <span>звітна нова <XMark on={doc.report_type === 'REPORTING_NEW'} /></span>
                <span>уточнююча <XMark on={doc.report_type === 'CORRECTING'} /></span>
              </div>
            </td>
          </tr>
          <tr>
            <td style={numCell}>2</td>
            <td style={nameCell}>Податковий (звітний) період</td>
            <td style={{ ...valCell, padding: '2px 6px' }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: '0.8rem', alignItems: 'center' }}>
                {(annual ? [4] : [1, 2, 3, 4]).map((q) => (
                  <span key={q}>{PERIOD_LABELS[q]} <XMark on={doc.period_quarter === q} /></span>
                ))}
                <b>{doc.period_year} року</b>
              </div>
            </td>
          </tr>
          {doc.report_type === 'CORRECTING' && (
            <tr>
              <td style={numCell}>3</td>
              <td style={nameCell}>Податковий (звітний) період, який уточнюється</td>
              <td style={{ ...valCell, padding: '2px 6px', fontSize: '0.8rem' }}>
                {doc.corrected_period_quarter ? PERIOD_LABELS[doc.corrected_period_quarter] : ''} {doc.corrected_period_year} року
              </td>
            </tr>
          )}
          <tr>
            <td style={numCell}>{legal ? 4 : 5}</td>
            <td style={nameCell}>{legal ? 'Платник податку (повне найменування)' : 'Платник податку (прізвище, ім’я, по батькові)'}</td>
            <td style={valCell}>{ro(org?.full_name || org?.name, 'left', print)}</td>
          </tr>
          <tr>
            <td style={numCell}>{legal ? '4.1' : 7}</td>
            <td style={nameCell}>{legal ? 'Код за ЄДРПОУ' : 'Реєстраційний номер облікової картки платника податків (РНОКПП)'}</td>
            <td style={valCell}>{ro(tin, 'center', print)}</td>
          </tr>
          {legal && (
            <tr>
              <td style={numCell}>4.2</td>
              <td style={nameCell}>Код виду економічної діяльності (КВЕД)</td>
              <td style={valCell}>{ro(fields.kved?.[0]?.code || org?.kved_main, 'center', print)}</td>
            </tr>
          )}
          <tr>
            <td style={numCell}>{legal ? '4.3' : 6}</td>
            <td style={nameCell}>Податкова адреса; телефон; електронна пошта</td>
            <td style={valCell}>{ro([org?.legal_address || org?.actual_address, org?.phone, org?.email].filter(Boolean).join(' · '), 'left', print)}</td>
          </tr>
          <tr>
            <td style={numCell}>{legal ? 5 : 4}</td>
            <td style={nameCell}>Найменування контролюючого органу, до якого подається декларація</td>
            <td style={valCell}>{ro(org?.sti_name, 'left', print)}</td>
          </tr>
          {!legal && (
            <tr>
              <td style={numCell}>8</td>
              <td style={nameCell}>Особливі відмітки</td>
              <td style={{ ...valCell, padding: '2px 6px', fontSize: '0.78rem' }}>
                <div>8.1 остання декларація у зв’язку з припиненням підприємницької діяльності{' '}
                  <Mark on={!!fields.m081} readOnly={readOnly} onToggle={() => onFields?.({ m081: !fields.m081 })} /></div>
                {!annual && (
                  <div>8.2 перехід на сплату інших податків і зборів у наступному періоді{' '}
                    <Mark on={!!fields.m082} readOnly={readOnly} onToggle={() => onFields?.({ m082: !fields.m082 })} /></div>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Розділ I ФОП: чисельність найманих + КВЕД (код і назва — редаговані). */
export function FopSectionI({ fields, onFields, readOnly, print }: {
  fields: SingleTaxFields; onFields?: (patch: Partial<SingleTaxFields>) => void;
  readOnly: boolean; print?: boolean;
}) {
  const kved = fields.kved ?? [];
  const setKved = (rows: { code: string; name: string }[]) => onFields?.({ kved: rows });
  const inp: CSSProperties = { width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
    fontFamily: SERIF, fontSize: '0.82rem', padding: '2px 4px', background: '#fefce8', color: '#111' };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 8 }}>
      <thead><tr><th colSpan={3} style={{ ...thBlank, fontWeight: 700, fontSize: '0.8rem' }}>I. Загальні показники підприємницької діяльності</th></tr></thead>
      <tbody>
        <tr>
          <td style={numCell}>9</td>
          <td style={nameCell}>Фактична чисельність найманих працівників у звітному періоді (осіб)</td>
          <td style={{ ...valCell, width: 130 }}>
            {readOnly ? ro(fields.staff_count ?? 0, 'center', print)
              : <input type="number" step="1" min="0" value={fields.staff_count ?? 0} style={{ ...inp, textAlign: 'center' }}
                  onChange={(e) => onFields?.({ staff_count: Number(e.target.value) || 0 })} />}
          </td>
        </tr>
        <tr>
          <td style={numCell}>10</td>
          <td style={{ ...nameCell }} colSpan={2}>
            <div style={{ marginBottom: 4 }}>Види підприємницької діяльності у звітному періоді</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...thBlank, width: 110 }}>Код КВЕД</th>
                <th style={thBlank}>Назва згідно з КВЕД</th>
                {!readOnly && <th style={{ ...thBlank, width: 30 }} />}
              </tr></thead>
              <tbody>
                {kved.map((k, i) => (
                  <tr key={i}>
                    <td style={valCell}>{readOnly ? ro(k.code, 'center', print)
                      : <input value={k.code} style={{ ...inp, textAlign: 'center' }} placeholder="62.01"
                          onChange={(e) => setKved(kved.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)))} />}</td>
                    <td style={valCell}>{readOnly ? ro(k.name, 'left', print)
                      : <input value={k.name} style={inp} placeholder="назва виду діяльності"
                          onChange={(e) => setKved(kved.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />}</td>
                    {!readOnly && (
                      <td style={{ ...valCell, width: 30, textAlign: 'center' }}>
                        <button type="button" className="btn-link" onClick={() => setKved(kved.filter((_, j) => j !== i))}>×</button>
                      </td>
                    )}
                  </tr>
                ))}
                {!readOnly && (
                  <tr><td colSpan={3} style={{ ...nameCell, textAlign: 'left' }}>
                    <button type="button" className="btn-link" onClick={() => setKved([...kved, { code: '', name: '' }])}>+ додати вид діяльності</button>
                  </td></tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function AdvancesRow({ label, prefix, values, setV, readOnly, print }: {
  label: string; prefix: 'R02' | 'R03'; values: Record<string, string>;
  setV?: (c: string, raw: string) => void; readOnly: boolean; print?: boolean;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 4 }}>
      <thead>
        <tr><th colSpan={4} style={{ ...thBlank }}>{label}: щомісячні авансові внески єдиного податку (грн, коп)</th></tr>
        <tr>{['I квартал', 'II квартал', 'III квартал', 'IV квартал'].map((q) => <th key={q} style={thBlank}>{q}</th>)}</tr>
      </thead>
      <tbody><tr>
        {[1, 2, 3, 4].map((i) => (
          <td key={i} style={valCell}><MoneyCell code={`${prefix}G${i}`} values={values} setV={setV} readOnly={readOnly} print={print} /></td>
        ))}
      </tr></tbody>
    </table>
  );
}

/** Усі розділи бланка ФОП (II–VIII). */
export function FopSections({ values, fields, setV, onFields, readOnly, print, group }: {
  values: Record<string, string>; fields: SingleTaxFields;
  setV?: (c: string, raw: string) => void; onFields?: (patch: Partial<SingleTaxFields>) => void;
  readOnly: boolean; print?: boolean; group: number | null;
}) {
  const months = fields.wz_months ?? [];
  const toggleMonth = (m: number) => onFields?.({
    wz_months: months.includes(m) ? months.filter((x) => x !== m) : [...months, m].sort((a, b) => a - b) });
  return (
    <div>
      {FOP_SECTIONS.map((s, idx) => (
        <div key={s.title}>
          {idx === 0 && <AdvancesRow label="II. Перша група" prefix="R02" values={values} setV={setV} readOnly={readOnly} print={print} />}
          {idx === 1 && <AdvancesRow label="III. Друга група" prefix="R03" values={values} setV={setV} readOnly={readOnly} print={print} />}
          <RowsTable title={s.title} headers={s.rows.some((r) => r.codes.length === 2) ? ['%', 'Обсяг (грн, коп)'] : ['Обсяг (грн, коп)']}
            rows={s.rows} values={values} setV={setV} readOnly={readOnly} print={print} />
        </div>
      ))}
      <RowsTable title="VII. Визначення зобов’язань із сплати єдиного внеску за даними звітного (податкового) періоду"
        headers={['Сума (грн, коп)']} rows={[FOP_VII]} values={values} setV={undefined} readOnly print={print} />
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 8 }}>
        <thead>
          <tr><th colSpan={12} style={{ ...thBlank, fontWeight: 700, fontSize: '0.8rem' }}>VIII. Визначення податкових зобов’язань по військовому збору — 1. Для платників першої, другої груп: відмітка про щомісячні авансові внески</th></tr>
          <tr>{MONTHS.map((_, i) => <th key={i} style={thBlank}>{String(i + 1).padStart(2, '0')}</th>)}</tr>
        </thead>
        <tbody><tr>
          {MONTHS.map((_, i) => (
            <td key={i} style={{ ...valCell, textAlign: 'center', width: 'auto' }}>
              <Mark on={months.includes(i + 1)} readOnly={readOnly || (group != null && group === 3)} onToggle={() => toggleMonth(i + 1)} />
            </td>
          ))}
        </tr></tbody>
      </table>
      <RowsTable title="" headers={['Сума (грн, коп)']} rows={[FOP_VIII_12]} values={values} setV={setV} readOnly={readOnly} print={print} />
      <RowsTable title="2. Для платників єдиного податку третьої групи" headers={['Сума (грн, коп)']}
        rows={FOP_VIII_3} values={values} setV={setV} readOnly={readOnly} print={print} />
    </div>
  );
}

/** Розділи II–V бланка юрособи. */
export function LegalSections({ values, setV, readOnly, print }: {
  values: Record<string, string>; setV?: (c: string, raw: string) => void; readOnly: boolean; print?: boolean;
}) {
  return (
    <div>
      {LEGAL_SECTIONS.map((s) => (
        <RowsTable key={s.title} title={s.title} headers={s.headers} rows={s.rows}
          values={values} setV={setV} readOnly={readOnly} print={print} />
      ))}
    </div>
  );
}

/** Додаток 1 — ЄСВ «за себе» по місяцях. */
export function EsvAppendix({ esv, onChange, readOnly, print, doc, org }: {
  esv: SingleTaxEsvAppendix; onChange?: (next: SingleTaxEsvAppendix) => void;
  readOnly: boolean; print?: boolean; doc: SingleTaxDeclaration; org: Organization | undefined;
}) {
  const rows = esv.rows ?? [];
  const totalBase = rows.reduce((s, r) => s + (Number(r.base) || 0), 0);
  const totalAmount = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const inp: CSSProperties = { width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
    fontFamily: SERIF, fontSize: '0.82rem', textAlign: 'right', padding: '2px 4px', background: '#fefce8', color: '#111' };
  const setRow = (i: number, patch: Partial<typeof rows[number]>) => {
    const next = rows.map((r, j) => (j === i ? { ...r, ...patch } : r));
    const r = next[i]!;
    if (patch.base != null || patch.rate != null) {
      r.amount = ((Number(r.base) || 0) * (Number(r.rate) || 0) / 100).toFixed(2);
    }
    onChange?.({ ...esv, rows: next });
  };
  return (
    <div style={{ ...blankBox }}>
      <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#555' }}>Додаток 1 до податкової декларації платника єдиного податку – фізичної особи – підприємця (F0133109)</div>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', padding: '4px 0 8px', fontFamily: SERIF }}>
        ВІДОМОСТІ про суми нарахованого доходу застрахованих осіб та суми нарахованого єдиного внеску
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
        <tbody>
          <tr><td style={nameCell}>Платник (РНОКПП, ПІБ)</td><td style={valCell}>{ro(`${org?.inn || org?.edrpou || ''} · ${org?.full_name || org?.name || ''}`, 'left', print)}</td></tr>
          <tr><td style={nameCell}>Період</td><td style={valCell}>{ro(`рік ${doc.period_year}; на спрощеній системі з ${esv.period_from ?? ''} по ${esv.period_to ?? ''}`, 'left', print)}</td></tr>
          <tr><td style={nameCell}>Код категорії застрахованої особи</td><td style={valCell}>{ro(esv.category_code ?? '6', 'center', print)}</td></tr>
          {!print && (
            <tr><td style={nameCell}>Подавати додаток у пакеті з декларацією</td>
              <td style={{ ...valCell, padding: '2px 6px' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.8rem' }}>
                  <input type="checkbox" checked={!!esv.include} disabled={readOnly}
                    onChange={(e) => onChange?.({ ...esv, include: e.target.checked })} /> так (рядок 21 декларації = «Усього» графи 4)
                </label>
              </td></tr>
          )}
        </tbody>
      </table>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={{ ...thBlank, textAlign: 'left' }}>9. Місяць</th>
          <th style={thBlank}>Самостійно визначена сума доходу (база), грн</th>
          <th style={{ ...thBlank, width: 90 }}>Розмір ЄСВ, %</th>
          <th style={thBlank}>Сума єдиного внеску до сплати, грн</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.month}>
              <td style={nameCell}>{MONTHS[r.month - 1]}</td>
              <td style={valCell}>{readOnly ? ro(print ? fmtMoney(r.base) : r.base, 'right', print)
                : <input type="number" step="0.01" value={r.base} style={inp} onChange={(e) => setRow(i, { base: e.target.value })} />}</td>
              <td style={valCell}>{readOnly ? ro(r.rate, 'center', print)
                : <input type="number" step="0.01" value={r.rate} style={{ ...inp, textAlign: 'center' }} onChange={(e) => setRow(i, { rate: e.target.value })} />}</td>
              <td style={valCell}>{readOnly ? ro(print ? fmtMoney(r.amount) : r.amount, 'right', print)
                : <input type="number" step="0.01" value={r.amount} style={inp} onChange={(e) => setRow(i, { amount: e.target.value })} />}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...nameCell, fontWeight: 700 }}>Усього</td>
            <td style={valCell}>{ro(fmtMoney(totalBase), 'right', print)}</td>
            <td style={valCell}>{ro('×', 'center', print)}</td>
            <td style={valCell}>{ro(fmtMoney(totalAmount), 'right', print)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Підписи (ФОП — сам платник; юрособа — керівник і головбух). */
export function SingleTaxSignatures({ doc, org, print }: { doc: SingleTaxDeclaration; org: Organization | undefined; print?: boolean }) {
  const legal = doc.form_code === 'J0103509';
  const dateStr = doc.document_date ? new Date(doc.document_date).toLocaleDateString('uk-UA') : '';
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 6 }}>
      <tbody>
        <tr>
          <td style={nameCell}>Дата подання</td>
          <td style={{ ...valCell, width: 130 }}>{ro(dateStr, 'center', print)}</td>
          <td style={nameCell}>{legal ? 'Керівник (уповноважена особа)' : 'Фізична особа – платник податку'}</td>
          <td style={{ ...valCell, width: 220 }}>{ro(legal ? org?.director_name : (org?.director_name || org?.full_name || org?.name), 'left', print)}</td>
          <td style={{ ...nameCell, width: 100 }}>РНОКПП</td>
          <td style={{ ...valCell, width: 130 }}>{ro(legal ? org?.director_inn : (org?.inn || ''), 'center', print)}</td>
        </tr>
        {legal && org && chiefForReports(org) ? (   /* ч.1179: лише з галочкою на картці */
          <tr>
            <td style={nameCell} colSpan={2} />
            <td style={nameCell}>Головний бухгалтер</td>
            <td style={valCell}>{ro(org.chief_accountant, 'left', print)}</td>
            <td style={nameCell}>РНОКПП</td>
            <td style={valCell}>{ro(org.chief_accountant_inn ?? null, 'center', print)}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}


// ── ч.1114: Додаток 2 — МПЗ (дзеркало single_tax_declaration_calc.compute_mpz) ──
export const MPZ_PAID_FOP: [string, string, string][] = [
  ['single_tax', '02.1', 'єдиний податок'],
  ['pdfo', '02.2', 'податок на доходи фізичних осіб (ПДФО)'],
  ['wz', '02.3', 'військовий збір'],
  ['pdfo_emp', '02.4', 'ПДФО з доходів фізичних осіб, які перебувають з платником єдиного податку у трудових або цивільно-правових відносинах'],
  ['wz_emp', '02.5', 'військовий збір з доходів фізичних осіб, які перебувають з платником єдиного податку у трудових або цивільно-правових відносинах'],
  ['pdfo_rent', '02.6', 'ПДФО з доходів за договорами оренди'],
  ['wz_rent', '02.7', 'військовий збір з доходів за договорами оренди'],
  ['land_tax', '02.8', 'земельний податок'],
  ['water_rent', '02.9', 'рентна плата за спеціальне використання води (у разі її сплати)'],
];
export const MPZ_PAID_LEGAL: [string, string, string][] = [
  ['single_tax', '02.1', 'єдиний податок'],
  ['profit_tax', '02.2', 'податок на прибуток'],
  ['pdfo_emp', '02.3', 'ПДФО з доходів фізичних осіб, які перебувають з платником єдиного податку у трудових або цивільно-правових відносинах'],
  ['wz_emp', '02.4', 'військовий збір з доходів фізичних осіб, які перебувають з платником єдиного податку у трудових або цивільно-правових відносинах'],
  ['pdfo_rent', '02.5', 'ПДФО з доходів за договорами оренди'],
  ['wz_rent', '02.6', 'військовий збір з доходів за договорами оренди'],
  ['land_tax', '02.7', 'земельний податок'],
  ['water_rent', '02.8', 'рентна плата за спеціальне використання води (у разі її сплати)'],
  ['wz', '02.9', 'військовий збір'],
];
const n = (v: string | number | undefined | null) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
export function mpzKDefault(year: number): string {
  return year === 2022 || year === 2023 ? '0.04' : year >= 2025 ? '0.057' : '0.05';
}
export function computeMpz(mpz: SingleTaxMpzAppendix | null | undefined, year: number, legal: boolean) {
  const rows = (mpz?.plots ?? []).map((r) => {
    const own = n(r.own_ha), rent = n(r.rent_ha), s = own + rent;
    const k = n(r.k || mpzKDefault(year)), m = n(r.months || 12);
    const mpzNgo = n(r.ngo) > 0 ? n(r.ngo) * k * m / 12 : 0;
    const mpzNoval = n(r.ngo) <= 0 && n(r.ngo_1ha) > 0 ? n(r.ngo_1ha) * s * k * m / 12 : 0;
    let total = mpzNgo + mpzNoval;
    if (year >= 2025 && s > 0) {
      const arable = (own * n(r.own_arable_pct) + rent * n(r.rent_arable_pct)) / s;
      total = Math.max(total, (arable >= 50 ? 1400 : 700) * s * m / 12);
    }
    return { mpzNgo, mpzNoval, total, rent20: n(r.rent_20pct), own, rent };
  });
  const sum = (f: (x: typeof rows[number]) => number) => rows.reduce((a, x) => a + f(x), 0);
  const paidMap = legal ? MPZ_PAID_LEGAL : MPZ_PAID_FOP;
  const paid = paidMap.reduce((a, [key]) => a + n(mpz?.paid?.[key]), 0);
  const total = sum((x) => x.total), rent20 = sum((x) => x.rent20);
  const diff = total - paid - rent20;
  return { rows, totals: { own: sum((x) => x.own), rent: sum((x) => x.rent), mpzNgo: sum((x) => x.mpzNgo),
    mpzNoval: sum((x) => x.mpzNoval), total, rent20 }, paid, diff, positive: Math.max(diff, 0) };
}

const EMPTY_PLOT = (year: number): SingleTaxMpzPlot => ({ cadastre: '', own_ha: '0', own_arable_pct: '100',
  rent_ha: '0', rent_arable_pct: '0', ngo: '0', ngo_1ha: '0', k: mpzKDefault(year), months: 12, rent_20pct: '0' });

/** Додаток 2 (ФОП F0133209) / Додаток МПЗ (юрособа J0135709). */
export function MpzAppendix({ mpz, onChange, readOnly, print, doc, legal }: {
  mpz: SingleTaxMpzAppendix; onChange?: (next: SingleTaxMpzAppendix) => void;
  readOnly: boolean; print?: boolean; doc: SingleTaxDeclaration; legal: boolean;
}) {
  const year = doc.period_year;
  const calc = computeMpz(mpz, year, legal);
  const inp: CSSProperties = { width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
    fontFamily: SERIF, fontSize: '0.78rem', textAlign: 'right', padding: '2px 3px', background: '#fefce8', color: '#111' };
  const th: CSSProperties = { ...thBlank, fontSize: '0.62rem' };
  const setPlot = (i: number, patch: Partial<SingleTaxMpzPlot>) =>
    onChange?.({ ...mpz, plots: mpz.plots.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const cell = (i: number, key: keyof SingleTaxMpzPlot, align: 'left' | 'right' = 'right', numeric = true) => {
    const v = mpz.plots[i]![key] as string | number;
    if (readOnly) return ro(print && numeric && key !== 'months' ? fmtMoney(v) : String(v ?? ''), align, print);
    return numeric
      ? <input type="number" step="any" value={v} style={{ ...inp, textAlign: align }} onChange={(e) => setPlot(i, { [key]: key === 'months' ? Number(e.target.value) : e.target.value } as Partial<SingleTaxMpzPlot>)} />
      : <input value={String(v ?? '')} style={{ ...inp, textAlign: align }} placeholder="кадастровий номер" onChange={(e) => setPlot(i, { [key]: e.target.value } as Partial<SingleTaxMpzPlot>)} />;
  };
  const paidMap = legal ? MPZ_PAID_LEGAL : MPZ_PAID_FOP;
  return (
    <div style={{ ...blankBox }}>
      <div style={{ textAlign: 'right', fontSize: '0.72rem', color: '#555' }}>
        {legal ? 'Додаток до податкової декларації платника єдиного податку третьої групи (юридичні особи) (J0135709)'
          : 'Додаток 2 до податкової декларації платника єдиного податку – фізичної особи – підприємця (F0133209)'}
      </div>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', padding: '4px 0 8px', fontFamily: SERIF }}>
        РОЗРАХУНОК загального мінімального податкового зобов’язання за податковий (звітний) {year} рік
      </div>
      {!print && (
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.8rem', marginBottom: 6 }}>
          <input type="checkbox" checked={!!mpz.include} disabled={readOnly}
            onChange={(e) => onChange?.({ ...mpz, include: e.target.checked })} />
          подавати додаток (є земельні ділянки сільгосппризначення у власності/оренді); позитивне значення р.04 →
          {legal ? ' р.11 ' : ' р.14.2 '}декларації
        </label>
      )}
      <div style={{ fontSize: '0.72rem', color: '#555', marginBottom: 4 }}>
        Розділ I. Одиниці виміру: гектари — з чотирма десятковими знаками, гривні — з двома. К = {mpzKDefault(year)}
        {' '}(0,05; 2022–2023 — 0,04; з 2025 — 0,057 до кінця року припинення воєнного стану). З 2025 МПЗ не менше 700 грн/га
        (1400 грн/га, якщо частка ріллі ≥ 50 %).
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>№</th><th style={th}>Кадастровий номер</th>
            <th style={th}>Власна, га</th><th style={th}>Частка ріллі, %</th>
            <th style={th}>Орендована, га</th><th style={th}>Частка ріллі, %</th>
            <th style={th}>НГО ділянки (НГОд), грн</th><th style={th}>НГО 1 га ріллі по області, грн</th>
            <th style={th}>К</th><th style={th}>М</th>
            <th style={th}>МПЗ (гр.7×К×М/12)</th><th style={th}>МПЗ (гр.8×S×К×М/12)</th>
            <th style={th}>Загальне МПЗ</th><th style={th}>20 % витрат на оренду</th>
            {!readOnly && <th style={{ ...th, width: 24 }} />}
          </tr>
          <tr>{Array.from({ length: 14 }, (_, i) => <th key={i} style={th}>{i + 1}</th>)}{!readOnly && <th style={th} />}</tr>
        </thead>
        <tbody>
          {mpz.plots.map((_, i) => (
            <tr key={i}>
              <td style={numCell}>1.{i + 1}</td>
              <td style={{ ...valCell, width: 170 }}>{cell(i, 'cadastre', 'left', false)}</td>
              <td style={valCell}>{cell(i, 'own_ha')}</td>
              <td style={valCell}>{cell(i, 'own_arable_pct')}</td>
              <td style={valCell}>{cell(i, 'rent_ha')}</td>
              <td style={valCell}>{cell(i, 'rent_arable_pct')}</td>
              <td style={valCell}>{cell(i, 'ngo')}</td>
              <td style={valCell}>{cell(i, 'ngo_1ha')}</td>
              <td style={{ ...valCell, width: 60 }}>{cell(i, 'k')}</td>
              <td style={{ ...valCell, width: 50 }}>{cell(i, 'months')}</td>
              <td style={valCell}>{ro(fmtMoney(calc.rows[i]!.mpzNgo), 'right', print)}</td>
              <td style={valCell}>{ro(fmtMoney(calc.rows[i]!.mpzNoval), 'right', print)}</td>
              <td style={valCell}>{ro(fmtMoney(calc.rows[i]!.total), 'right', print)}</td>
              <td style={valCell}>{cell(i, 'rent_20pct')}</td>
              {!readOnly && (
                <td style={{ ...valCell, width: 24, textAlign: 'center' }}>
                  <button type="button" className="btn-link" onClick={() => onChange?.({ ...mpz, plots: mpz.plots.filter((_, j) => j !== i) })}>×</button>
                </td>
              )}
            </tr>
          ))}
          <tr>
            <td style={numCell}>2</td>
            <td style={nameCell}>Усього</td>
            <td style={valCell}>{ro(calc.totals.own.toFixed(4), 'right', print)}</td><td style={valCell}>×</td>
            <td style={valCell}>{ro(calc.totals.rent.toFixed(4), 'right', print)}</td><td style={valCell}>×</td>
            <td style={valCell}>×</td><td style={valCell}>×</td><td style={valCell}>×</td><td style={valCell}>×</td>
            <td style={valCell}>{ro(fmtMoney(calc.totals.mpzNgo), 'right', print)}</td>
            <td style={valCell}>{ro(fmtMoney(calc.totals.mpzNoval), 'right', print)}</td>
            <td style={valCell}>{ro(fmtMoney(calc.totals.total), 'right', print)}</td>
            <td style={valCell}>{ro(fmtMoney(calc.totals.rent20), 'right', print)}</td>
            {!readOnly && <td style={valCell} />}
          </tr>
        </tbody>
      </table>
      {!readOnly && (
        <button type="button" className="btn-link" style={{ marginTop: 4 }}
          onClick={() => onChange?.({ ...mpz, plots: [...mpz.plots, EMPTY_PLOT(year)] })}>+ додати земельну ділянку</button>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
        <thead>
          <tr><th colSpan={3} style={{ ...thBlank, fontWeight: 700, fontSize: '0.8rem' }}>Розділ II</th></tr>
          <tr><th style={{ ...thBlank, textAlign: 'left' }}>Назва показника</th><th style={{ ...thBlank, width: 52 }}>Код рядка</th><th style={{ ...thBlank, width: 130 }}>Сума</th></tr>
        </thead>
        <tbody>
          <tr><td style={nameCell}>Загальне мінімальне податкове зобов’язання (ЗМПЗ) (графа 13 рядка 2 розділу I)</td><td style={numCell}>01</td><td style={valCell}>{ro(fmtMoney(calc.totals.total), 'right', print)}</td></tr>
          <tr><td style={nameCell}>Загальна сума сплачених податків, зборів, платежів протягом податкового (звітного) року, у тому числі</td><td style={numCell}>02</td><td style={valCell}>{ro(fmtMoney(calc.paid), 'right', print)}</td></tr>
          {paidMap.map(([key, code, label]) => (
            <tr key={key}>
              <td style={{ ...nameCell, paddingLeft: 18 }}>{label}</td><td style={numCell}>{code}</td>
              <td style={valCell}>{readOnly ? ro(print ? fmtMoney(mpz.paid?.[key]) : (mpz.paid?.[key] ?? ''), 'right', print)
                : <input type="number" step="0.01" value={mpz.paid?.[key] ?? ''} style={inp}
                    onChange={(e) => onChange?.({ ...mpz, paid: { ...(mpz.paid ?? {}), [key]: e.target.value } })} />}</td>
            </tr>
          ))}
          <tr><td style={nameCell}>20 відсотків витрат на сплату орендної плати (рядок 2 графи 14 розділу I)</td><td style={numCell}>03</td><td style={valCell}>{ro(fmtMoney(calc.totals.rent20), 'right', print)}</td></tr>
          <tr><td style={{ ...nameCell, fontWeight: 700 }}>Різниця між ЗМПЗ та сумою сплачених податків і витрат на оренду (рядок 01 − рядок 02 − рядок 03) (+/−); позитивне значення переноситься до рядка {legal ? '11' : '14.2'} декларації</td><td style={numCell}>04</td><td style={valCell}>{ro(fmtMoney(calc.diff), 'right', print)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

export { B };
